import axios from 'axios'
import laroute from '~/laroute'
import store from '~/store'
import moment from 'moment'

// state
export const state = {
  list: [],
  one: null,
  allowedList: [],
  current: {},
  filtersData: {
    partners: [],
    salesReps: [],
    shipTo: [],
    printPickList: [
      'Not printed',
      'Printed'
    ],
    invoiceStatuses: [],
    orderStatuses: [],
    terms: [],
    shippers: []
  },
  pickList: [],
  total: 0,

  countries: [],
  provinces: [],
  addressList: [],
  customerSOList: [],
  order: {},
  hasPrepaidInvoice: false,
  pdf: undefined,
  salesOrderItem: {
    id: 0
  }
}

// getters
export const getters = {
  list: state => state.list,
  one: state => state.one,
  allowedList: state => state.allowedList,
  total: state => state.total,
  current: state => state.current.so_items,
  currentServicesAndFreight: state => state.current.so_services_freight,
  filtersData: state => state.filtersData,
  pickList: state => state.pickList,
  countries: state => state.countries,
  provinces: state => state.provinces,
  addressList: state => state.addressList,
  customerSOList: state => state.customerSOList,
  hasPrepaidInvoice: state => state.hasPrepaidInvoice,
  pdf: state => state.pdf,
  order: state => state.order,
  salesOrderItem: state => state.salesOrderItem
}

// mutations
export const mutations = {
  ADD_ITEM (state, data) {
    state.current = data
  },
  LIST (state, data) {
    state.list = data
  },
  ONE (state, data) {
    state.one = data
  },
  ALLOWED_LIST (state, data) {
    state.allowedList = data
  },
  CURRENT (state, data) {
    state.current = data
  },
  CURRENT_ITEMS (state, data) {
    state.current.so_items = data
  },
  TOTAL (state, data) {
    state.total = data
  },
  FILTERS_DATA (state, data) {
    state.filtersData = data
  },
  PICK_LIST (state, data) {
    state.pickList = data
  },
  COUNTRIES (state, data) {
    state.countries = data
  },
  PROVINCES (state, data) {
    state.provinces = data
  },
  SHIP_TO (state, data) {
    state.filtersData.shipTo = data
  },
  ADDRESS_LIST (state, data) {
    state.addressList = data
  },

  CUSTOMER_SO_LIST (state, data) {
    state.customerSOList = data
  },

  ORDER (state, data) {
    state.order = data
  },

  HAS_PREPAID_INVOICE (state, data) {
    state.hasPrepaidInvoice = data
  },

  PDF (state, data) {
    state.pdf = data
  },

  SALES_ORDER_ITEM (state, data) {
    state.salesOrderItem = data
  }
}

// actions
export const actions = {
  async addItem ({ commit }, data) {
    const response = await axios.post(laroute.route('create_so', { companyAbbr: store.getters['auth/company'] }), data)
    commit('CURRENT', response.data)
  },

  async fetchAll ({ commit }, filterData) {
    await axios.get(laroute.route('filter_so', { companyAbbr: store.getters['auth/company'], ...filterData })).then((res) => {
      res.data.items.map((so) => {
        so.created_at = moment(so.created_at).format('YYYY-MM-DD')
        so.due_date = moment(so.due_date).format('YYYY-MM-DD')
        so.picklist_status = so.is_pick_list_printed ? 'Printed' : 'Not Printed'
      })
      commit('LIST', res.data.items)
      commit('TOTAL', res.data.total)
    })
  },

  async fetchOne ({ commit }, id) {
    if (state.current && +state.current.id === +id) {
      commit('CURRENT', state.current)
    } else {
      const { data } = await axios.get(laroute.route('get_so', { companyAbbr: store.getters['auth/company'], id }))
      commit('CURRENT', data)
    }
  },

  async getOne ({ commit }, id) {
    const { data } = await axios.get(laroute.route('get_so_one', { companyAbbr: store.getters['auth/company'], id }))
    commit('ONE', data)
  },

  async fetchFiltersData ({ commit }) {
    let payload = {
      partners: state.filtersData.partners,
      shipTo: state.filtersData.shipTo,
      printPickList: [
        { id: 0, value: 'Not printed' },
        { id: 1, value: 'Printed' }
      ]
    }

    if (!payload.partners.length) {
      const partnersResponse = await axios.get(laroute.route('customers_list'))
      payload.partners = partnersResponse.data
    }

    commit('FILTERS_DATA', payload)
  },
  async updateItem ({ commit }, { data, id, services }) {
    await axios.patch(laroute.route('update-sales-order', { id }), { data, services })
  },
  async updateNonTangibleProductDescription ({ commit }, data) {
    await axios.patch(laroute.route('update_non_tangible_product_description', {
      companyAbbr: data.companyAbbr,
      productId: data.productId
    }), {
      description: data.description
    })
  },
  async saveNewTangibleItems ({ commit }, { id, items }) {
    await axios.patch(laroute.route('create_new_tangible_so_items', {
      companyAbbr: store.getters['auth/company'], id
    }), { items })
  },
  async getSOPickList ({ commit }, { companyAbbr, SOid }) {
    const { data } = await axios.get(laroute.route('get_so_pick_list', {
      companyAbbr, id: SOid
    }))

    commit('PICK_LIST', data)
  },
  async fetchCountriesList ({ commit }) {
    const { data } = await axios.get(laroute.route('countries_list'))
    commit('COUNTRIES', data)
  },
  async fetchProvincesList ({ commit }) {
    const { data } = await axios.get(laroute.route('get_all_provinces'))
    commit('PROVINCES', data)
  },

  async fetchCustomerSOList ({ commit }, { customerID }) {
    const { data } = await axios.get(laroute.route('get_customer_so_list', { companyAbbr: store.getters['auth/company'], customerID }))
    commit('CUSTOMER_SO_LIST', data)
  },

  async deleteSO ({ commit }, { id, reason }) {
    await axios.patch(laroute.route('delete_sales_order', {
      companyAbbr: store.getters['auth/company'], id
    }), { reason })
  },

  async deleteSOItem ({ commit }, { id }) {
    await axios.delete(laroute.route('delete_so_item', { id }))
  },

  async localSOUpdate ({ commit }, so) {
    commit('CURRENT_ITEMS', Object.assign({}, so))
    return so
  },

  async changePrintedStatus ({ commit }, { id, status }) {
    await axios.patch(laroute.route('change-printed-status', { id }), { status })
  },

  async getSOWithPicklists ({ commit }) {
    const { data } = await axios.get(laroute.route('get-sales-orders-with-picklist',
      { companyAbbr: store.getters['auth/company'] }))

    commit('LIST', data)
  },

  async getSalesOrdersByIds ({ commit }, ids) {
    const { data } = await axios.get(laroute.route('get-sales-orders-by-ids',
      { companyAbbr: store.getters['auth/company'], ids }))
    commit('ALLOWED_LIST', data)
  },

  async changeStatus ({ commit }, { id }) {
    await axios.patch(laroute.route('update-so-status',
      { id: id }))
  },

  async fetchItemsForWO ({ commit }, id) {
    const { data } = await axios.get(laroute.route('get-so-data-for-wo', { id }))

    commit('ORDER', data)
  },

  async checkPrepaidInvoice ({ commit }, { id }) {
    const { data } = await axios.get(laroute.route('check-prepaid-invoice', { id }))

    commit('HAS_PREPAID_INVOICE', Boolean(data))
  },

  async generatePDF ({ commit }, id) {
    const { data } = await axios.get(laroute.route('sales-order-generate-pdf',
      { companyAbbr: store.getters['auth/company'], id }),
    { responseType: 'arraybuffer' })

    commit('PDF', data)
  },

  async getSalesOrderItemByPartNumberAndSalesOrder ({ commit }, { soId, itemMasterId }) {
    const { data } = await axios.get(laroute.route('get-sales-order-item-by-part-number-and-sales-order',
      { companyAbbr: store.getters['auth/company'], soId, itemMasterId }))

    commit('SALES_ORDER_ITEM', data)
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/store/modules/salesOrder.js